import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import { ShoppingBag, Star, Check, ChevronDown } from 'lucide-react';
import { PRODUCT_IMAGES } from '../../constants';

const badges = ['Natural Ingredients', 'Made in USA', 'Premium Quality'];

export default function HeroSection() {
  const [ref, inView] = useInView(0.1);

  return (
    <section
      data-testid="hero-section"
      className="flex flex-col md:flex-row md:items-stretch"
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '92vh',
        position: 'relative',
        overflow: 'hidden'
      }}>

      {/* Mobile-only: bottle as full-bleed background */}
      <div
        className="md:hidden"
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <img
          src={PRODUCT_IMAGES.singleBottle}
          alt=""
          aria-hidden="true"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            opacity: 0.35,
          }}
        />
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(247,247,245,0.1) 0%, rgba(247,247,245,0.6) 60%, rgba(247,247,245,1) 100%)',
        }} />
      </div>

      {/* Left: Text Panel */}
      <div
        ref={ref}
        className={`fade-up ${inView ? 'visible' : ''} w-full md:w-1/2 flex items-center order-2 md:order-1`}
        style={{
          position: 'relative',
          zIndex: 2,
          padding: 'clamp(48px, 6vw, 80px) clamp(24px, 4vw, 56px) clamp(32px, 4vw, 80px) clamp(24px, 6vw, 80px)'
        }}>

        <div style={{ maxWidth: '520px' }}>
          <span className="overline block mb-5">Premium Male Formula</span>
          <h1
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(2.4rem, 5vw, 4rem)',
              color: '#0D1A2E',
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              marginBottom: '22px'
            }}>
            Reload Your<br />
            <span style={{ color: '#1B3D7E' }}>Performance.</span><br />
            Reignite Drive.
          </h1>
          <p style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '1.05rem',
            color: '#64748B',
            lineHeight: 1.8,
            maxWidth: '460px',
            marginBottom: '28px'
          }}>
            A premium natural formula designed to support semen volume, libido, and male reproductive health. Powered by clinically studied botanical extracts.
          </p>

          {/* Star social proof */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={15} fill="#1B3D7E" stroke="#1B3D7E" />)}
            </div>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.82rem', color: '#64748B', fontWeight: 600 }}>
              4.9/5 — Over 2,000 verified reviews
            </span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link
              to="/shop"
              data-testid="hero-primary-cta"
              className="btn-blue gap-2 px-8 py-4 rounded-full text-sm"
              style={{ fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}>
              <ShoppingBag size={16} strokeWidth={2} style={{ marginRight: '6px' }} />
              Buy Now — £24.99
            </Link>
            <a
              href="#benefits"
              data-testid="hero-secondary-cta"
              className="btn-outline-blue gap-2 px-8 py-4 rounded-full text-sm"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Learn More
              <ChevronDown size={16} style={{ marginLeft: '6px' }} />
            </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-2">
            {badges.map((b) =>
            <span
              key={b}
              data-testid={`trust-badge-${b.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#1B3D7E',
                fontWeight: 700,
                backgroundColor: 'rgba(27,61,126,0.07)',
                border: '1px solid rgba(27,61,126,0.15)',
                padding: '5px 14px',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <Check size={10} strokeWidth={3} />
                {b}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right: Full-height image panel — desktop only */}
      <div
        className={`fade-up delay-2 ${inView ? 'visible' : ''} hidden md:block md:w-1/2 relative order-2`}
        style={{
          minHeight: '340px',
          background: 'linear-gradient(135deg, rgba(27,61,126,0.04) 0%, rgba(27,61,126,0.1) 100%)',
          overflow: 'hidden'
        }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(27,61,126,0.1) 0%, transparent 65%)',
          pointerEvents: 'none'
        }} />
        <img
          src={PRODUCT_IMAGES.singleBottle}
          alt="RELOAD Male Performance Formula Bottle"
          data-testid="hero-product-image"
          className="animate-float"
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            padding: '28px',
            filter: 'drop-shadow(0 24px 60px rgba(13,26,46,0.2))'
          }} />
      </div>
    </section>);

}